import React, { useState, useEffect } from 'react';
import { X, Calendar, Package, AlertTriangle } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const GenerateInvoiceModal = ({ isOpen, onClose, onGenerate, existingInvoices, shipments, editMode = false, invoiceToEdit = null }) => {
  const [selectedMonth, setSelectedMonth] = useState('');
  const [formData, setFormData] = useState({
    invoiceDate: new Date()?.toISOString()?.split('T')?.[0],
    description: '',
    tonnage: '',
    feeRate: '3.25',
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0],
  });
  const [exchangeRate, setExchangeRate] = useState(83.50); // USD to INR
  const [showReplaceConfirmation, setShowReplaceConfirmation] = useState(false);
  const [existingInvoiceForMonth, setExistingInvoiceForMonth] = useState(null);
  const [availableMonths, setAvailableMonths] = useState([]);

  const USD_TO_INR = 83.50;
  const USD_TO_AUD = 1.52; // 1 USD = 1.52 AUD

  // Pre-populate form when editing
  useEffect(() => {
    if (editMode && invoiceToEdit) {
      setSelectedMonth(invoiceToEdit?.invoiceMonth);
      setFormData({
        invoiceDate: invoiceToEdit?.invoiceDate || invoiceToEdit?.date,
        description: invoiceToEdit?.description,
        tonnage: invoiceToEdit?.tonnage?.toString(),
        feeRate: invoiceToEdit?.feeRate?.toString(),
        dueDate: invoiceToEdit?.dueDate,
      });
      setExchangeRate(invoiceToEdit?.exchangeRate || 83.50);
    }
  }, [editMode, invoiceToEdit]);

  // Generate list of available months (current month and 11 months back)
  useEffect(() => {
    const months = [];
    const currentDate = new Date();
    
    for (let i = 0; i < 12; i++) {
      const monthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const monthKey = `${monthDate?.getFullYear()}-${String(monthDate?.getMonth() + 1)?.padStart(2, '0')}`;
      
      // Check if month already has approved invoice
      const hasApprovedInvoice = existingInvoices?.some(
        inv => inv?.invoiceMonth === monthKey && inv?.approvalStatus === 'approved'
      );
      
      // Only add month if no approved invoice exists
      if (!hasApprovedInvoice) {
        months?.push({
          value: monthKey,
          label: monthDate?.toLocaleDateString('en-US', { year: 'numeric', month: 'long' }),
          date: monthDate
        });
      }
    }
    
    setAvailableMonths(months);
  }, [existingInvoices]);

  // Auto-populate tonnage when month is selected
  useEffect(() => {
    // Skip auto-population if in edit mode
    if (editMode) return;
    
    if (selectedMonth) {
      // Calculate tonnage from shipments that arrived (discharged/completed) in the selected month
      const [year, month] = selectedMonth?.split('-');
      const dischargedShipments = shipments?.filter(shipment => {
        if (shipment?.status !== 'discharged' && shipment?.status !== 'completed') return false;
        
        const eta = new Date(shipment?.eta);
        return eta?.getFullYear() === parseInt(year) && 
               eta?.getMonth() + 1 === parseInt(month);
      });
      
      const totalTonnage = dischargedShipments?.reduce((sum, shipment) => sum + shipment?.tonnage, 0) || 0;
      
      setFormData(prev => ({
        ...prev,
        tonnage: totalTonnage?.toString(),
        description: `Coal Trading Management Services - ${availableMonths?.find(m => m?.value === selectedMonth)?.label}`
      }));

      // Check if non-approved invoice already exists for this month
      const existingInvoice = existingInvoices?.find(
        inv => inv?.invoiceMonth === selectedMonth && inv?.approvalStatus !== 'approved'
      );
      
      if (existingInvoice) {
        setExistingInvoiceForMonth(existingInvoice);
        setShowReplaceConfirmation(true);
      } else {
        setExistingInvoiceForMonth(null);
        setShowReplaceConfirmation(false);
      }
    }
  }, [selectedMonth, shipments, existingInvoices, availableMonths, editMode]);

  const calculateAmounts = (tons, rate) => {
    const usdAmount = tons * rate;
    const inrAmount = usdAmount * USD_TO_INR;
    const audAmount = usdAmount * USD_TO_AUD;
    return { 
      usdAmount: parseFloat(usdAmount?.toFixed(2)), 
      inrAmount: parseFloat(inrAmount?.toFixed(2)),
      audAmount: parseFloat(audAmount?.toFixed(2))
    };
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (!selectedMonth && !editMode) {
      alert('Please select a month for the invoice');
      return;
    }

    const tonnage = parseFloat(formData?.tonnage);
    const feeRate = parseFloat(formData?.feeRate);
    const amountUSD = tonnage * feeRate;
    
    // VALIDATION: Prevent saving invoice with zero amount
    if (amountUSD === 0 || isNaN(amountUSD)) {
      alert('⚠️ Cannot save invoice: Invoice amount cannot be zero. Please check tonnage and fee rate.');
      return;
    }
    
    // VALIDATION: Prevent saving invoice with quantity less than 60,000 MT
    if (tonnage < 60000) {
      alert('⚠️ Cannot save invoice: Quantity must be at least 60,000 MT. Current quantity: ' + tonnage?.toLocaleString() + ' MT');
      return;
    }
    
    const amountAUD = amountUSD * USD_TO_AUD;
    const amountINR = amountUSD * exchangeRate;
    
    // Get month label for display
    const monthLabel = availableMonths?.find(m => m?.value === selectedMonth)?.label || selectedMonth;
    
    const invoice = {
      // Preserve ID when editing, generate new ID when creating
      id: editMode && invoiceToEdit?.id ? invoiceToEdit?.id : `RNR-${new Date()?.getFullYear()}-${String(Math.floor(Math.random() * 1000))?.padStart(3, '0')}`,
      client: 'RNR Steels and Metals',
      shipmentRef: `MONTHLY-${selectedMonth}`,
      description: formData?.description,
      invoiceDate: formData?.invoiceDate,
      date: formData?.invoiceDate, // Add date field for table display
      dueDate: formData?.dueDate,
      tonnage: tonnage,
      feeRate: feeRate,
      amountUSD: amountUSD,
      usdAmount: amountUSD, // Add usdAmount field for table display
      amountAUD: amountAUD,
      audAmount: amountAUD, // Add audAmount field for table display
      amountINR: amountINR,
      inrAmount: amountINR, // Add inrAmount field for table display
      exchangeRate: exchangeRate,
      currency: 'USD',
      // Preserve status when editing, set to pending when creating
      status: editMode && invoiceToEdit?.status ? invoiceToEdit?.status : 'pending',
      approvalStatus: editMode && invoiceToEdit?.approvalStatus ? invoiceToEdit?.approvalStatus : 'pending',
      paymentTerms: 'Net 30',
      invoiceMonth: selectedMonth,
      month: monthLabel, // Add month label for table display
      generatedAt: editMode && invoiceToEdit?.generatedAt ? invoiceToEdit?.generatedAt : new Date()?.toISOString()
    };
    
    onGenerate?.(invoice);
    resetForm();
    onClose?.();
  };

  const handleReplaceConfirm = () => {
    setShowReplaceConfirmation(false);
    // Continue with form submission
  };

  const handleReplaceCancel = () => {
    setSelectedMonth('');
    setShowReplaceConfirmation(false);
    setExistingInvoiceForMonth(null);
  };

  const resetForm = () => {
    setSelectedMonth('');
    setFormData({
      invoiceDate: new Date()?.toISOString()?.split('T')?.[0],
      description: '',
      tonnage: '',
      feeRate: '3.25',
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0],
    });
    setShowReplaceConfirmation(false);
    setExistingInvoiceForMonth(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {editMode ? 'Edit Invoice' : 'Generate Monthly Invoice'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {editMode 
                ? `Editing invoice ${invoiceToEdit?.id} for RNR Steels and Metals` 
                : 'Create month-based invoice for RNR Steels and Metals'}
            </p>
          </div>
          <button
            onClick={() => {
              resetForm();
              onClose();
            }}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Replace Confirmation Alert */}
        {showReplaceConfirmation && existingInvoiceForMonth && !editMode && (
          <div className="mx-6 mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-yellow-900">Invoice Already Exists</h3>
                <p className="text-sm text-yellow-800 mt-1">
                  An invoice for this month already exists (Invoice #{existingInvoiceForMonth?.id}) with status: <span className="font-medium">{existingInvoiceForMonth?.approvalStatus}</span>.
                  <br />
                  Do you want to replace it with a new invoice?
                </p>
                <div className="flex gap-2 mt-3">
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-yellow-700 border-yellow-300 hover:bg-yellow-100"
                    onClick={handleReplaceConfirm}
                  >
                    Yes, Replace
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-gray-700 border-gray-300 hover:bg-gray-100"
                    onClick={handleReplaceCancel}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Month Selection */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-600" />
              Select Invoice Month
            </h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Invoice Month <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select a month...</option>
                {availableMonths?.map(month => (
                  <option key={month?.value} value={month?.value}>
                    {month?.label}
                  </option>
                ))}
              </select>
              {availableMonths?.length === 0 && (
                <p className="text-sm text-amber-600 mt-1">
                  All available months already have approved invoices
                </p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Only months without approved invoices are shown
              </p>
            </div>
          </div>

          {/* Invoice Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-600" />
              Invoice Details
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Invoice Date
                </label>
                <Input
                  type="date"
                  value={formData?.invoiceDate}
                  onChange={(e) => setFormData({ ...formData, invoiceDate: e?.target?.value })}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Due Date
                </label>
                <Input
                  type="date"
                  value={formData?.dueDate}
                  onChange={(e) => setFormData({ ...formData, dueDate: e?.target?.value })}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="3"
                placeholder="Coal Trading Management Services for the month of..."
                value={formData?.description}
                onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
                required
              />
            </div>
          </div>

          {/* Calculation Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Package className="h-5 w-5 text-green-600" />
              Calculation Details
            </h3>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                Tonnage is automatically calculated from shipments that arrived at destination port (discharged/completed status) during the selected month
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tonnage (MT) - Editable
                </label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder={!selectedMonth ? "Select month first" : "Auto-calculated, edit if needed"}
                  value={formData?.tonnage}
                  onChange={(e) => setFormData({ ...formData, tonnage: e?.target?.value })}
                  required
                />
                {!selectedMonth && (
                  <p className="text-xs text-gray-500 mt-1">
                    Select a month to auto-populate tonnage
                  </p>
                )}
                {selectedMonth && formData?.tonnage === '0' && (
                  <p className="text-xs text-amber-600 mt-1">
                    No shipments arrived at destination in selected month - enter manually
                  </p>
                )}
                {selectedMonth && formData?.tonnage && formData?.tonnage !== '0' && (
                  <p className="text-xs text-green-600 mt-1">
                    ✓ Auto-calculated from destination arrivals - edit if needed
                  </p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fee Rate (USD/MT)
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData?.feeRate}
                  onChange={(e) => setFormData({ ...formData, feeRate: e?.target?.value })}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Exchange Rate (USD to INR)
              </label>
              <Input
                type="number"
                step="0.01"
                value={exchangeRate}
                onChange={(e) => setExchangeRate(parseFloat(e?.target?.value))}
                required
              />
            </div>
          </div>

          {formData?.tonnage && formData?.feeRate && (
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="text-sm font-semibold text-blue-900 mb-3">Calculated Amounts</h4>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-blue-700">Amount (USD):</span>
                  <span className="text-lg font-bold text-blue-900">
                    ${calculateAmounts(parseFloat(formData?.tonnage), parseFloat(formData?.feeRate))?.usdAmount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-blue-200">
                  <span className="text-sm text-blue-700">Amount (AUD):</span>
                  <span className="text-base font-semibold text-blue-800">
                    A${calculateAmounts(parseFloat(formData?.tonnage), parseFloat(formData?.feeRate))?.audAmount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-blue-200">
                  <span className="text-sm text-blue-700">Amount (INR):</span>
                  <span className="text-base font-semibold text-blue-800">
                    ₹{calculateAmounts(parseFloat(formData?.tonnage), parseFloat(formData?.feeRate))?.inrAmount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                <p className="text-xs text-blue-600 mt-2">
                  Exchange Rates: 1 USD = ₹{USD_TO_INR} | 1 USD = A${USD_TO_AUD}
                </p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                resetForm();
                onClose();
              }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700"
              disabled={!selectedMonth || showReplaceConfirmation}
            >
              {editMode ? 'Update Invoice' : 'Generate Invoice'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GenerateInvoiceModal;